import PageHero from "@/components/PageHero";
import useSEO from "@/hooks/useSEO";
import Reveal from "@/components/Reveal";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Search, ClipboardCheck, Compass, Rocket, Repeat, TrendingUp, ArrowUpRight } from "lucide-react";

const STEPS = [
  { n: "01", icon: Search, title: "Discovery", desc: "Understanding your business goals, audience, and competitive landscape.", color: "saavi-blue" },
  { n: "02", icon: ClipboardCheck, title: "Audit", desc: "Evaluating your current digital presence, funnel and analytics.", color: "saavi-red" },
  { n: "03", icon: Compass, title: "Strategy", desc: "A customized growth roadmap tied to revenue outcomes.", color: "saavi-green" },
  { n: "04", icon: Rocket, title: "Execution", desc: "Launching campaigns, creative, and marketing assets at speed.", color: "saavi-blue" },
  { n: "05", icon: Repeat, title: "Optimization", desc: "Continuous testing — creatives, audiences, landing pages.", color: "saavi-red" },
  { n: "06", icon: TrendingUp, title: "Scale", desc: "Doubling-down on what works to maximize ROI & growth.", color: "saavi-green" },
];

export default function Process() {
  useSEO();
  return (
    <>
      <PageHero
        eyebrow="Our Process"
        title={<>The <span className="text-saavi-blue">Growth Framework.</span></>}
        description="A repeatable 6-step process used across every account — from discovery to scale."
        accent="green"
      />

      <section className="section-pad border-b border-border">
        <div className="container-saavi">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {STEPS.map(({ n, icon: Icon, title, desc, color }, i) => (
              <Reveal key={n} variant="fade-up" delay={i * 110}>
                <div
                  data-testid={`process-step-${n}`}
                  className={`card-hover lift-on-hover ai-card relative border border-border rounded-md p-7 bg-card overflow-hidden h-full`}
                >
                  <span className={`absolute top-0 left-0 h-1 w-full bg-${color}`} />
                  <div className="flex items-baseline justify-between">
                    <span className={`font-extrabold text-5xl tracking-tighter text-${color}`}>{n}</span>
                    <Icon className={`h-6 w-6 text-${color} transition-transform duration-500 hover:rotate-6 hover:scale-110`} />
                  </div>
                  <h3 className="mt-6 text-2xl">{title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-saavi">
          <div className="border border-border rounded-md p-10 md:p-14 bg-card">
            <div className="font-mono-overline text-saavi-red mb-4">Lead Generation Flow</div>
            <h2 className="text-3xl md:text-4xl mb-10">Traffic → Engagement → Conversion → Nurturing → Sales</h2>
            <div className="flex flex-wrap items-center gap-2">
              {["Traffic", "Engagement", "Conversion", "Nurturing", "Sales"].map((s, i, arr) => (
                <div key={s} className="flex items-center gap-2">
                  <span className="px-4 py-2.5 border border-border rounded-md font-semibold text-sm bg-background">{s}</span>
                  {i < arr.length - 1 && <span className="text-muted-foreground">→</span>}
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/contact"><Button size="lg" className="h-12 px-6 gap-2">Map My Funnel <ArrowUpRight className="h-4 w-4" /></Button></Link>
              <Link to="/case-studies"><Button size="lg" variant="outline" className="h-12 px-6">See Results</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
